/**
 * GenericDetailPanel — Multi-mode detail panel (slide-over / modal / fullscreen)
 * that works for ANY doctype. Renders the full rich detail page (tabs, form,
 * Save button, image) inside the panel shell — same experience as Item DetailPanel.
 *
 * Reuses the exact same Radix Dialog + Framer Motion shell as the Item DetailPanel.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, PanelRight, Maximize2, Fullscreen } from "lucide-react";
import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/features/pos/hooks/useReducedMotion";
import { useMediaQuery } from "@/hooks/useMediaQuery";

import { RIGHT_RAIL_WIDTH } from "@/components/layout/AppRailRight";
import { RAIL_WIDTH, PANEL_WIDTH } from "@/components/layout/ErpSidebar";
import { useErpLayout } from "@/app/layouts/ErpLayoutContext";
import { DetailPageTabsProvider } from "@/contexts/DetailPageTabsContext";
import { GenericDetailContent } from "./GenericDetailContent";
import { buildDetailRoute, isMappedDoctype } from "@/lib/config/docRoutes";

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type PanelMode = "slide-over" | "modal" | "fullscreen";

const STORAGE_KEY = "zvg-detail-panel-mode";
const OPEN_ANIM_MS = 300;
const MODE_ORDER: PanelMode[] = ["slide-over", "modal", "fullscreen"];

// Doctypes whose rich custom detail page is preferred over the generic full-page
// view when going full-screen (e.g. Customer's loyalty/orders/spend page). Other
// mapped doctypes (Item, Sales Order) keep the polished ?generic=1 full-page.
const FULLSCREEN_CUSTOM_PAGE_DOCTYPES = new Set(["Customer", "Pricing Rule"]);

// Doctypes whose slide-over opens at half the viewport width (they render a
// richer two-column bespoke page in the panel, so 640px is too tight).
const HALF_WIDTH_DOCTYPES = new Set(["Pricing Rule"]);

function getInitialMode(): PanelMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "slide-over" || stored === "modal" || stored === "fullscreen") return stored;
  return "slide-over";
}

// ---------------------------------------------------------------------------
// CSS class helpers (same as Item DetailPanel)
// ---------------------------------------------------------------------------

function getContentClasses(mode: PanelMode, wide: boolean, halfWidth = false): string {
  const base = "fixed outline-none motion-reduce:!duration-0";
  switch (mode) {
    case "slide-over":
      return cn(
        base,
        "z-20 inset-y-0 flex flex-col",
        wide
          ? "w-[75%] max-lg:max-w-full bg-background border-l shadow-lg"
          : halfWidth
            ? "w-[50%] max-lg:max-w-full bg-background border-l shadow-lg"
            : "w-full max-w-[640px] max-lg:max-w-full bg-background border-l shadow-lg",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:duration-300 data-[state=closed]:duration-200",
        "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
      );
    case "modal":
      return cn(
        base,
        "z-50 inset-0 flex items-center justify-center",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:duration-300 data-[state=closed]:duration-200",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      );
    case "fullscreen":
      return cn(
        base,
        "z-20 inset-y-0 flex flex-col bg-background",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:duration-200 data-[state=closed]:duration-150",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      );
  }
}

function getInnerClasses(mode: PanelMode, wide: boolean): string {
  // Card is the sole scroll container — overscroll-y-contain prevents wheel
  // events from chaining to the document/list behind the panel.
  const scroll = "overflow-y-auto overscroll-y-contain scrollbar-hide";
  switch (mode) {
    case "slide-over":
      return `h-full ${scroll}`;
    case "modal":
      return cn(
        `w-full shadow-apple-xl ${scroll}`,
        wide
          ? "lg:w-[75%] lg:max-h-[85vh] lg:rounded-2xl"
          : "lg:max-w-[800px] lg:max-h-[85vh] lg:rounded-2xl",
        "max-lg:max-w-full max-lg:max-h-full max-lg:rounded-none",
      );
    case "fullscreen":
      return `w-full h-full ${scroll}`;
  }
}

// ---------------------------------------------------------------------------
// Layout mode switcher
// ---------------------------------------------------------------------------

const MODES = [
  { value: "slide-over" as const, icon: PanelRight, label: "Slide-over" },
  { value: "modal" as const, icon: Maximize2, label: "Modal" },
  { value: "fullscreen" as const, icon: Fullscreen, label: "Full-screen" },
];

export type DetailPanelMode = PanelMode;
export const DETAIL_PANEL_MODE_KEY = STORAGE_KEY;
export function LayoutModeSwitcher({ mode, onModeChange }: { mode: PanelMode; onModeChange: (m: PanelMode) => void }) {
  return (
    <div className="flex items-center gap-0.5" role="group" aria-label="Layout mode">
      {MODES.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => onModeChange(value)}
          aria-label={label}
          aria-pressed={mode === value}
          className={cn(
            "rounded-md p-1.5 transition-colors",
            mode === value
              ? "bg-neutral-100 dark:bg-neutral-800 text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-neutral-50 dark:hover:bg-neutral-900",
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GenericDetailPanel
// ---------------------------------------------------------------------------

export interface GenericDetailPanelProps {
  open: boolean;
  onClose: () => void;
  doctype: string;
  docName: string;
  docLabel?: string;
  /** When true, uses a wider panel (e.g. ~1100px) for two-column layouts */
  wide?: boolean;
}

export function GenericDetailPanel({
  open, onClose, doctype, docName, docLabel, wide = false,
}: GenericDetailPanelProps) {
  const [mode, setMode] = useState<PanelMode>(() => {
    // If stored preference is fullscreen, start as slide-over instead
    // (fullscreen navigates to full-page route — no panel needed)
    const stored = getInitialMode();
    return stored === "fullscreen" ? "slide-over" : stored;
  });
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { isCollapsed: sidebarCollapsed } = useErpLayout();
  const cardRef = useRef<HTMLDivElement>(null);

  // Dispatch panel-scroll for bottom nav morph (ref-based, no React state)
  useEffect(() => {
    if (isDesktop || !open) return;
    const timer = setTimeout(() => {
      const el = cardRef.current;
      if (!el) return;
      const onScroll = () => {
        window.dispatchEvent(new CustomEvent('panel-scroll', { detail: { scrollTop: el.scrollTop } }));
      };
      el.addEventListener("scroll", onScroll, { passive: true });
      (cardRef as any)._cleanup = () => el.removeEventListener("scroll", onScroll);
    }, 100);
    return () => {
      clearTimeout(timer);
      (cardRef as any)._cleanup?.();
    };
  }, [isDesktop, open]);

  const handleModeChange = useCallback((newMode: PanelMode) => {
    if (newMode === "fullscreen") {
      // Navigate FIRST — do NOT call onClose() because it triggers
      // setSearchParams which races with navigate and overwrites the URL.
      localStorage.setItem(STORAGE_KEY, newMode);
      // Doctypes with a richer bespoke page (Customer) go to that page on
      // full-screen; everything else uses the generic full-page view so it
      // matches the panel's GenericDetailContent.
      const target =
        FULLSCREEN_CUSTOM_PAGE_DOCTYPES.has(doctype) && isMappedDoctype(doctype)
          ? buildDetailRoute(doctype, docName)
          : `/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(docName)}?generic=1`;
      navigate(target);
      return;
    }
    setMode(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }, [onClose, navigate, doctype, docName]);

  // Framer Motion layout animation gate
  const hasOpened = useRef(false);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        hasOpened.current = true;
        forceUpdate((n) => n + 1);
      }, OPEN_ANIM_MS + 50);
      return () => clearTimeout(timer);
    } else {
      hasOpened.current = false;
    }
  }, [open]);

  // Modal click-outside-to-close.
  // Only treat a pointer-down as a backdrop click when it lands DIRECTLY on the
  // Content element (the dimmed area around the card). Without the
  // `e.target === e.currentTarget` guard, a pointer-down inside a NESTED dialog
  // (e.g. the Attach Files picker opened from the gallery) also satisfies
  // "outside cardRef" and wrongly closes the whole panel — TKT-2026-00143:
  // clicking Add Image → interacting with the picker booted the user back to
  // the item list. onInteractOutside/onFocusOutside are already prevented for
  // the same nested-portal reason; this closes the last hole (modal mode only).
  const handleContentPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (
        mode === "modal" &&
        e.target === e.currentTarget &&
        cardRef.current &&
        !cardRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    },
    [mode, onClose],
  );

  // Keyboard shortcuts: Cmd/Ctrl + [ and ] to cycle modes
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const idx = MODE_ORDER.indexOf(mode);
      if (e.key === "]") {
        e.preventDefault();
        handleModeChange(MODE_ORDER[(idx + 1) % MODE_ORDER.length]);
      } else if (e.key === "[") {
        e.preventDefault();
        handleModeChange(MODE_ORDER[(idx - 1 + MODE_ORDER.length) % MODE_ORDER.length]);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, mode, handleModeChange]);

  const enableLayout = hasOpened.current;
  const springTransition = reducedMotion
    ? { layout: { duration: 0 } }
    : { layout: { type: "spring" as const, damping: 25, stiffness: 300 } };
  const crossfadeDuration = reducedMotion ? 0 : 0.15;

  const displayLabel = docLabel || docName;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
      modal={mode === "modal"}
    >
      <DialogPrimitive.Portal>
        {mode === "modal" && (
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-50 bg-black/20 transition-opacity duration-200 motion-reduce:!duration-0",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=open]:duration-200 data-[state=closed]:duration-150",
            )}
          />
        )}

        <DialogPrimitive.Content
          aria-label={`${doctype} detail: ${displayLabel}`}
          className={getContentClasses(mode, wide, HALF_WIDTH_DOCTYPES.has(doctype))}
          style={
            mode === "slide-over"
              ? (isDesktop
                ? { right: `${RIGHT_RAIL_WIDTH}px` }
                : { left: 0, right: 0, width: '100vw' })
              : mode === "fullscreen"
                ? { left: isDesktop ? `${RAIL_WIDTH + (sidebarCollapsed ? 0 : PANEL_WIDTH)}px` : 0, right: isDesktop ? `${RIGHT_RAIL_WIDTH}px` : 0 }
                : undefined
          }
          onPointerDown={handleContentPointerDown}
          onInteractOutside={(e) => {
            // Always prevent — nested portals (MetaFieldEditModal, dropdowns)
            // trigger "interact outside" and would close the panel.
            // Modal backdrop-click is handled by handleContentPointerDown instead.
            e.preventDefault();
          }}
          onFocusOutside={(e) => {
            // Always prevent — focus moving to nested portal dialogs
            // (MetaFieldEditModal, select dropdowns) must not close the panel.
            e.preventDefault();
          }}
        >
          <DialogPrimitive.Title className="sr-only">
            {doctype} detail: {displayLabel}
          </DialogPrimitive.Title>

          <motion.div
            ref={cardRef}
            className={cn("flex flex-col bg-background pb-14 md:pb-0", getInnerClasses(mode, wide))}
            layout={enableLayout}
            transition={springTransition}
          >
            {/* Header — plain sticky bar (no morph animation) */}
            <div
              className={cn(
                "shrink-0 sticky top-0 z-10 flex items-center justify-between h-14 border-b border-border/40 bg-background",
                isDesktop
                  ? (mode === "fullscreen" ? "px-6" : "px-4")
                  : "px-3",
              )}
            >
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors gap-0.5 text-sm md:-ml-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="md:inline hidden">Back to list</span>
                <span className="md:hidden">Back</span>
              </button>

              <div className="flex-1 min-w-0 text-center mx-2 md:mx-4">
                <h2 className="text-sm font-semibold truncate">{displayLabel}</h2>
              </div>

              <div className="flex items-center gap-1">
                <div className="hidden md:flex">
                  <LayoutModeSwitcher mode={mode} onModeChange={handleModeChange} />
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-muted-foreground/70 hover:text-foreground rounded-md p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                  aria-label="Close panel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body — card scrolls this content; no nested scroll container */}
            <div
              className={cn(
                mode === "fullscreen" && !wide && "flex justify-center",
              )}
            >
              <div className={cn(
                mode === "fullscreen" && !wide && "max-w-[860px] w-full",
                mode === "fullscreen" && wide && "w-full",
              )}>
                <AnimatePresence mode="wait">
                  {docName && (
                    <motion.div
                      key={docName}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: crossfadeDuration }}
                    >
                      <DetailPageTabsProvider>
                        <GenericDetailContent
                          doctype={doctype}
                          docName={docName}
                          onClose={onClose}
                        />
                      </DetailPageTabsProvider>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
